import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ProcessAddOns.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import AddOnRowsEditor, {
  ADD_ON_ROW_MODE, addOnRowErrors, addOnRowsToProcessRequest, formatMaxPerEvent, processAddOnToRow,
} from '../add-on/AddOnRowsEditor';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import useAddOns, { invalidateAddOns } from '../../hooks/useAddOns';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import { formatAddOnPrice, formatGroupPrices } from '../../util/addOns';
import { samePrice } from '../../util/eventPrices';
import ProcessNotice from './ProcessNotice';
import { apiErrorMessage, eventNouns } from './processUtils';

const PREVIEW_POPUP = 'processaddonspreview';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Group prices of a row as a comparable list (sorted by group id).
const groupPriceList = (list) => (Array.isArray(list) ? list : [])
  .filter(p => p?.participantGroupId != null && p.price != null)
  .map(p => ({ id: Number(p.participantGroupId), price: Number(p.price) }))
  .sort((a, b) => a.id - b.id);

const sameGroupPriceList = (a, b) => {
  const left = groupPriceList(a);
  const right = groupPriceList(b);
  return left.length === right.length && left.every((p, i) => p.id === right[i].id && samePrice(p.price, right[i].price));
};

// R10: only removals and price / group price changes touch bookings (max and Homepage do not) → only then the
// preview is needed before saving.
function changesBookings(original, request) {
  const byId = new Map(request.filter(r => r.id != null).map(r => [Number(r.id), r]));
  return (original ?? []).some(o => {
    const next = byId.get(Number(o.id));
    if (!next) return true;
    return !samePrice(o.price, next.price) || !sameGroupPriceList(o.participantGroupPrices, next.participantGroupPrices);
  });
}

// "3 Buchungen entfernt · 1 Anmeldewunsch entfernt · 4 Teilnahmen neu berechnet" ('' when nothing happens).
function effectText({ removedBookings = 0, removedWishes = 0, repriced = 0 }) {
  return [
    removedBookings > 0 ? `${plural(removedBookings, 'Buchung', 'Buchungen')} entfernt` : null,
    removedWishes > 0 ? `${plural(removedWishes, 'Anmeldewunsch', 'Anmeldewünsche')} entfernt` : null,
    repriced > 0 ? `${plural(repriced, 'Teilnahme', 'Teilnahmen')} neu berechnet` : null,
  ].filter(Boolean).join(' · ');
}

/**
 * "Zusätze anpassen": edit the sign-up add-ons of ONE process (copied from its template + catalog at creation;
 * all events of the process share them) — add-on, price, max per event, Homepage and group prices per row.
 * Save → when a row was removed or a price / group price changed, POST /EventProcess/{id}/PreviewAddOns first:
 * a popup lists removed bookings / registration wishes and repriced participants of the upcoming events (only
 * when something is affected), then PUT /EventProcess/{id}/AddOns (full list). Shown inline instead of the list.
 */
function ProcessAddOnsEditor({ process, catalog, groups, groupsById, onSaved, onCancel }) {
  const { openPopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const [rows, setRows] = useState(() => (process.addOns ?? []).map(processAddOnToRow));
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // { request, preview } while the preview popup is open.
  const [pending, setPending] = useState(null);
  const topRef = useRef(null);
  const nouns = eventNouns(process);

  // Catalog once, when the editor opens the first time (shared list, see hooks/useAddOns).
  const ensureCatalog = catalog.ensureLoaded;
  useEffect(() => { ensureCatalog(); }, [ensureCatalog]);

  const fail = (err, fallback) => {
    closeSuccessBar();
    setSaving(false);
    setError(apiErrorMessage(err, fallback));
    topRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  };

  const save = (request) => {
    setSaving(true);
    setError('');
    startSuccessBarLoading('Zusätze werden gespeichert');
    api.put(`/EventProcess/${process.id}/AddOns`, { addOns: request })
      .then(response => {
        // The catalog's usage counts changed (rows added/removed) — refetched on its next load.
        invalidateAddOns();
        setSaving(false);
        startSuccessBarSuccess('Zusätze gespeichert');
        onSaved(response.data);
      })
      .catch(err => fail(err, 'Zusätze konnten nicht gespeichert werden.'));
  };

  const handleSave = () => {
    if (saving) return;
    setShowErrors(true);
    if (addOnRowErrors(rows, { mode: ADD_ON_ROW_MODE.process, groupsById }).size > 0) {
      setError('Bitte die markierten Einträge prüfen.');
      topRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      return;
    }
    const request = addOnRowsToProcessRequest(rows);
    if (!changesBookings(process.addOns, request)) {
      save(request);
      return;
    }
    setSaving(true);
    setError('');
    startSuccessBarLoading('Auswirkungen werden geprüft');
    api.post(`/EventProcess/${process.id}/PreviewAddOns`, { addOns: request })
      .then(response => {
        const preview = response.data ?? {};
        const total = Number(preview.repricedParticipantCount ?? 0)
          + Number(preview.removedBookingCount ?? 0)
          + Number(preview.removedRegistrationWishCount ?? 0);
        // Nothing affected (no upcoming events / no bookings) → save directly.
        if (Number(preview.affectedEventCount ?? 0) === 0 || total === 0) {
          save(request);
          return;
        }
        closeSuccessBar();
        setSaving(false);
        setPending({ request, preview });
        openPopup(PREVIEW_POPUP, process.id);
      })
      .catch(err => fail(err, 'Die Auswirkungen konnten nicht geprüft werden.'));
  };

  const preview = pending?.preview ?? null;
  const previewItems = (preview?.items ?? []).filter(i =>
    Number(i.removedBookings ?? 0) + Number(i.removedRegistrationWishes ?? 0) + Number(i.repricedParticipants ?? 0) > 0);
  const affectedEvents = Number(preview?.affectedEventCount ?? 0);
  const previewWarnings = Array.isArray(preview?.warnings) ? preview.warnings.filter(Boolean) : [];
  const affectedWord = affectedEvents === 1
    ? `1 kommenden ${nouns.one}`
    : `${affectedEvents} kommende ${nouns.many}`;

  return (
    <div className={'processAddOnsEditor'} ref={topRef}>
      <div className={'processAddOnsEditorHeader'}>
        <h3>Zusätze anpassen</h3>
        <p className={'processActionDesc'}>
          Gilt für alle {nouns.many} dieses Prozesses. Neue Zusätze übernehmen die Katalogwerte
          {process.locationName ? ` für ${process.locationName}` : ' der Prozess-Location'}; „Homepage“ bietet sie
          zusätzlich in den Anmeldeformularen und über den persönlichen Link an. Max. je {nouns.one} 0 = unbegrenzt.
          Entfernte Zusätze und geänderte Preise wirken auf offene Anmeldungen kommender {nouns.many} – vorher siehst
          du, wer betroffen ist.
        </p>
      </div>

      {error && <ProcessNotice tone={'error'}>{error}</ProcessNotice>}
      {catalog.failed && (
        <ProcessNotice tone={'warning'}>Zusätze konnten nicht geladen werden – die Auswahl ist leer.</ProcessNotice>
      )}

      <AddOnRowsEditor
        mode={ADD_ON_ROW_MODE.process}
        rows={rows}
        onChange={setRows}
        addOns={catalog.addOns}
        addOnsLoaded={catalog.loaded}
        addOnsFailed={catalog.failed}
        groups={groups}
        locationId={process.locationId ?? null}
        locationName={process.locationName ?? ''}
        eventNoun={nouns.one}
        showErrors={showErrors}
        disabled={saving}
        emptyText={'Keine Zusätze – über „Zusatz hinzufügen“ eintragen.'}
      />

      <div className={'processAddOnsEditorFooter'}>
        <ActionButton icon={'close'} label={'Abbrechen'} onClick={onCancel} disabled={saving} />
        <ActionButton icon={'save'} label={'Speichern'} type={2} onClick={handleSave} disabled={saving} />
      </div>

      <Popup
        name={PREVIEW_POPUP}
        headerText={'Zusätze ändern?'}
        confirmText={'Speichern'}
        type={'warning'}
        onConfirm={() => { if (pending) save(pending.request); }}
        withCancel
        wide
      >
        <p>Die Änderung betrifft {affectedWord} dieses Prozesses:</p>
        {previewItems.length > 0 && (
          <ul className={'processAddOnsPreviewList'}>
            {previewItems.map(i => (
              <li key={i.addOnId} className={'processAddOnsPreviewRow'}>
                <span className={'processAddOnsPreviewName'}>{i.addOnName}</span>
                <span className={'processAddOnsPreviewEffect'}>
                  {effectText({
                    removedBookings: Number(i.removedBookings ?? 0),
                    removedWishes: Number(i.removedRegistrationWishes ?? 0),
                    repriced: Number(i.repricedParticipants ?? 0),
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
        {/* Totals: repriced participants are counted once even if several add-ons change. */}
        {(previewItems.length !== 1) && (
          <p className={'processAddOnsPreviewTotal'}>
            {previewItems.length > 1 ? 'Insgesamt: ' : ''}
            {effectText({
              removedBookings: Number(preview?.removedBookingCount ?? 0),
              removedWishes: Number(preview?.removedRegistrationWishCount ?? 0),
              repriced: Number(preview?.repricedParticipantCount ?? 0),
            })}
          </p>
        )}
        {previewWarnings.length > 0 && (
          <ProcessNotice tone={'warning'}>
            <ul className={'processAddOnsPreviewWarnings'}>
              {previewWarnings.map((text, i) => <li key={i}>{text}</li>)}
            </ul>
          </ProcessNotice>
        )}
        <p className={'formFieldHint'}>
          Entfernt werden Buchungen bei Teilnehmern, auf der Warteliste, bei Abgemeldeten und in offenen Anmeldungen
          der Inbox. Bezahlte und manuell gesetzte Preise bleiben unverändert; vergangene, abgeschlossene und abgesagte{' '}
          {nouns.many} ebenso.
        </p>
      </Popup>
    </div>
  );
}

/**
 * Section "Zusätze" of the process page (after "Pauschalen & Abzüge"): one compact row per sign-up add-on of the
 * process (name, price + group prices, max per event, Homepage); "Anpassen" swaps in the inline editor — also on
 * closed processes (nothing is affected there). The add-on catalog is fetched when the editor opens the first
 * time and reused for every process; groups come from the shared group list. onSaved(EventProcessResponse) →
 * the page takes over the process.
 */
function ProcessAddOns({ process, onSaved }) {
  const catalog = useAddOns();
  const { groups, byId: groupsById } = useParticipantGroups();
  // Id of the process being edited — another selected process never shows the editor.
  const [editingId, setEditingId] = useState(null);
  const editing = editingId != null && editingId === process.id;
  const nouns = eventNouns(process);

  // Leave edit mode when another process is selected (and do not reopen it on the way back).
  useEffect(() => { setEditingId(null); }, [process.id]);

  const addOns = useMemo(() => process.addOns ?? [], [process.addOns]);

  const renderRow = (a) => {
    const groupText = formatGroupPrices(a, groups);
    return (
      <li key={a.id} className={'processAddOnRow'}>
        <span className={'material-symbols-rounded processAddOnIcon'} aria-hidden={'true'}>inventory_2</span>
        <span className={'processAddOnName'}>{a.addOnName}</span>
        <span className={'processAddOnPrice'}>{formatAddOnPrice(a.price)}</span>
        <span className={'processAddOnMeta'}>
          {[
            groupText || null,
            formatMaxPerEvent(a.maxQuantity, nouns.one),
            a.bookableOnHomepage ? 'auch über die Homepage' : 'nur durch die Orga',
          ].filter(Boolean).join(' · ')}
        </span>
      </li>
    );
  };

  return (
    <div className={'processAddOnsSection'}>
      <div className={'processSectionHeader'}>
        <label className={'innerContainerLabel'}>Zusätze</label>
        {!editing && (
          <ActionButton
            icon={'tune'}
            label={'Anpassen'}
            type={3}
            onClick={() => setEditingId(process.id)}
            title={'Zusätze anpassen'}
            ariaLabel={'Zusätze anpassen'}
          />
        )}
      </div>

      {editing ? (
        <ProcessAddOnsEditor
          key={process.id}
          process={process}
          catalog={catalog}
          groups={groups}
          groupsById={groupsById}
          onSaved={detail => { setEditingId(null); onSaved(detail); }}
          onCancel={() => setEditingId(null)}
        />
      ) : addOns.length === 0 ? (
        <p className={'processAddOnsEmpty'}>Keine Zusätze in diesem Prozess.</p>
      ) : (
        <div className={'processAddOnsTable'}>
          <ul className={'processAddOnList'} aria-label={'Zusätze'}>
            {addOns.map(renderRow)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default ProcessAddOns;
